import React from "react";
import "./About.css";
import "./FirstDataModel.css";
import { Paper } from "@mui/material";
import ReactAudioPlayer from "react-audio-player";
import env from "../../env/env";
import blendkey from "../../images/about/blendkey.png";
import heterogeneitykey from "../../images/about/heterogeneitykey.png";
import segregationkey from "../../images/about/segregationkey.png";
import stratificationkey from "../../images/about/stratificationkey.png";
import antiphonalkey from "../../images/about/antiphonalkey.png";
import shiftskey from "../../images/about/shiftskey.png";
import sectionalkey from "../../images/about/sectionalkey.png";
import progressivekey from "../../images/about/progressivekey.png";
import gesturekey from "../../images/about/gesturekey.png";

export default function About() {
  return (
    <div className="about--container">
      <header className="about--header">
        <h1>Data Model 1</h1>
      </header>
      <div className="about--content">
        <h2>Orchestral effects and auditory grouping processes</h2>
        <p>
          Timbre emerges from the perceptual fusion of acoustical components
          into a single auditory event in concurrent grouping. According to
          Sandell (1995), the results of instrumental combinations include two
          types of <strong>blend</strong>: timbral augmentation (dominant
          instrument is embellished by subservient instruments) and emergence
          (synthesizing a new timbre that is identified as none of its
          constituent instruments). Heterogeneity results when sounds are not
          blended and separately identifiable.
        </p>
        <p>
          In sequential grouping, timbre is also involved in auditory stream
          integration (the perceptual connection of events into a melodic
          stream) and <strong>segregation</strong> of events into different
          streams based on timbral differences that may signal multiple sound
          sources. We also propose that the interaction of concurrent and
          sequential grouping results in <strong>stratification</strong> of
          musical material into different foreground and background layers of
          more or less prominence.
        </p>
        <p>
          Timbre is also implicated in segmental grouping. Discontinuities
          promote chunking of musical units, called{" "}
          <strong>orchestral contrasts</strong> with varying orchestration
          (e.g., antiphonal contrasts, timbral echoes), whereas a succession of
          gradually changing blended or integrated timbres creates a sense of
          continuity through <strong>progressive</strong> orchestration, which
          would be chunked as one unit (e.g., orchestration, which would be
          chunked as one unit (e.g., Klangfarbenmelodie).
        </p>
        <p>
          Table 1 provides an overview of the orchestral effect categories based
          on the grouping processes involved. Each description contains a link
          to a representative example, which contains a detailed view of the
          annotation, a view of the score, and a sound clip.
        </p>
        <br />
        <Paper className="fdm--paper">
          <h3 className="about--title">
            Table 1 - Orchestral effects, subtypes, and descriptions arranged by
            grouping processes
          </h3>
          <table className="fdm--table">
            <tr>
              <th>Grouping Process</th>
              <th>Orchestral Effect</th>
              <th>Subtypes and descriptions</th>
              <th>Analysis Key</th>
            </tr>
            <tr className="group">
              <td rowspan="5">Concurrent</td>
              <td rowspan="5">Blend</td>
              <td>
                <p>
                  <strong>Augmentation</strong> involves one instrument
                  embellishing another, with one instrument dominating the
                  combination (Sandell, 1995).
                </p>
                <p>ex. Debussy - La Mer Mvt.I</p>
                <ReactAudioPlayer
                  src={
                    env.BACKEND_API_URL +
                    "/staticfiles/effect/first_data_model/excerpts/ble2442.wav"
                  }
                  controls
                />
              </td>
              <td>
                <img src={blendkey} alt="" />
              </td>
            </tr>
            <tr className="group">
              <td>
                <p>
                  <strong>Emergence</strong> involves synthesizing a new timbre
                  that is identified as none of its constituent instruments
                  (Sandell, 1995).
                </p>
                <p>ex. Debussy - La Mer Mvt.III</p>
                <ReactAudioPlayer
                  src={
                    env.BACKEND_API_URL +
                    "/staticfiles/effect/first_data_model/excerpts/ble2524.wav"
                  }
                  controls
                />
              </td>
              <td>
                <img src={blendkey} alt="" />
              </td>
            </tr>
            <tr className="group">
              <td>
                <p>
                  <strong>Heterogeneity</strong> involves cases where
                  instruments are doubled at intervals that correspond to the
                  harmonic series (P1, P8, P5, etc.), starting and stopping
                  together and playing in parallel in terms of pitch and
                  dynamics (i.e., they look on the score like they should
                  blend), but they are heard more or less independently.
                </p>
                <p>ex. Debussy - La Mer Mvt.III</p>
                <ReactAudioPlayer
                  src={
                    env.BACKEND_API_URL +
                    "/staticfiles/effect/first_data_model/excerpts/ble2528.wav"
                  }
                  controls
                />
              </td>
              <td>
                <img src={heterogeneitykey} alt="" />
              </td>
            </tr>
            <tr className="group">
              <td>
                <p>
                  <strong>Punctuation blend</strong> occurs when a vertical
                  sonority with several instruments involves a synchronous onset
                  for a single chord, often at a higher dynamic for
                  accentuation. It also is often of fairly short duration so
                  that the listener doesn't have time to analyze the constituent
                  instruments.
                </p>
                <p>ex. Mahler - Symphony No.1 Mvt.IV</p>
                <ReactAudioPlayer
                  src={
                    env.BACKEND_API_URL +
                    "/staticfiles/effect/first_data_model/excerpts/ble2343.wav"
                  }
                  controls
                />
              </td>
              <td>
                <img src={blendkey} alt="" />
              </td>
            </tr>
            <tr className="group">
              <td>
                <p>
                  <strong>Textural integration</strong> occurs when two or more
                  instruments have different material (i.e., contrasting
                  rhythmic figures or pitch material) but integrate to create a
                  single textural layer. It is perceived as being more than a
                  single instrument (i.e., emergence or augmentation), but less
                  than two segregated layers.
                </p>
                <p>ex. Mendelssohn - Symphony No.3 Mvt.II</p>
                <ReactAudioPlayer
                  src={
                    env.BACKEND_API_URL +
                    "/staticfiles/effect/first_data_model/excerpts/ble2212.wav"
                  }
                  controls
                />
              </td>
              <td>
                <img src={blendkey} alt="" />
              </td>
            </tr>
            <tr>
              <td>Sequential</td>
              <td>Segregation</td>
              <td>
                <p>
                  <strong>Segregation</strong> involves clearly distinguishable
                  voices with nearly equivalent prominence or salience. The
                  different instrumental parts must be coequal, often scored as
                  contrapuntal melodic lines with rhythmic independence.
                  Normally segregation occurs with individual instruments,
                  although totally fused instrument pairings or groupings could
                  also constitute a "virtual" voice.
                </p>
                <p>ex. Berlioz - Symphonie Fantastique Mvt.IV</p>
                <ReactAudioPlayer
                  src={
                    env.BACKEND_API_URL +
                    "/staticfiles/effect/first_data_model/excerpts/seg2209.wav"
                  }
                  controls
                />
              </td>
              <td>
                <img src={segregationkey} alt="" />
              </td>
            </tr>
            <tr className="group">
              <td>Concurrent/Sequential</td>
              <td>Stratification</td>
              <td>
                <p>
                  <strong>Stratification</strong> creates two or more layers of
                  musical material, separated into more and less prominent
                  strands (notions of foreground, middleground, and background).
                  Stratified layers often have more than one instrument in at
                  least one of the layers, which have varying levels of
                  prominence.
                </p>
                <p>ex. Debussy - La Mer Mvt.I</p>
                <ReactAudioPlayer
                  src={
                    env.BACKEND_API_URL +
                    "/staticfiles/effect/first_data_model/excerpts/str2196.wav"
                  }
                  controls
                />
              </td>
              <td>
                <img src={stratificationkey} alt="" />
              </td>
            </tr>
            <tr>
              <td rowspan="5">Segmental</td>
              <td rowspan="5">Orchestral contrasts</td>
              <td>
                <p>
                  <strong>Antiphonal contrasts</strong> sections require call
                  and response type phrase structure, where the response is
                  musically related as a consequence to the call material, but
                  not the same. Each alternating musical unit is scored with
                  different instrumentation.
                </p>
                <p>ex. Schubert - Symphony No.9 Mvt.I</p>
                <ReactAudioPlayer
                  src={
                    env.BACKEND_API_URL +
                    "/staticfiles/effect/first_data_model/excerpts/con2005.wav"
                  }
                  controls
                />
              </td>
              <td>
                <img src={antiphonalkey} alt="" />
              </td>
            </tr>
            <tr>
              <td>
                <p>
                  <strong>Timbral echoes</strong> involve a repeated musical
                  phrase or idea with different orchestrations. This is distinct
                  from an antiphonal figure, because each group plays the same
                  musical idea (echoing each other), rather than playing an
                  antecedent or consequent phrase; however, one group seems more
                  "distant" than the other.
                </p>
                <p>
                  ex. Mussorgsky, Ravel - Pictures at An Exhibition Mvt.XIII
                </p>
                <ReactAudioPlayer
                  src={
                    env.BACKEND_API_URL +
                    "/staticfiles/effect/first_data_model/excerpts/con2408.wav"
                  }
                  controls
                />
              </td>
              <td>
                <img src={antiphonalkey} alt="" />
              </td>
            </tr>
            <tr>
              <td>
                <p>
                  <strong>Timbral shifts</strong> can be conceived of as an
                  orchestral "hot potato" or timbral variation, wherein musical
                  materials are reiterated with varying orchestrations (i.e., a
                  repeated phrase is "passed around" the orchestra).
                  Perceptually it is similar to timbral modulation, but is
                  presented in discrete steps, rather than as a seamless
                  coherent grouping.
                </p>
                <p>ex. Brahms - Symphony No.4 Mvt.II</p>
                <ReactAudioPlayer
                  src={
                    env.BACKEND_API_URL +
                    "/staticfiles/effect/first_data_model/excerpts/con2316.wav"
                  }
                  controls
                />
              </td>
              <td>
                <img src={shiftskey} alt="" />
              </td>
            </tr>
            <tr>
              <td>
                <p>
                  <strong>Sectional boundaries</strong> involve changes in
                  timbre, which cause segmentation (chunking) of sequences.
                  Large-scale sections in music are formed on the basis of
                  similarities in register, texture and instrumentation (i.e.,
                  timbre). Ergo, timbre change leads to a boundary creation,
                  while timbre similarity leads to chunking of events into
                  coherent units.
                </p>
                <p>ex. Schubert - Symphony No.9 Mvt.IV</p>
                <ReactAudioPlayer
                  src={
                    env.BACKEND_API_URL +
                    "/staticfiles/effect/first_data_model/excerpts/con2086.wav"
                  }
                  controls
                />
              </td>
              <td>
                <img src={sectionalkey} alt="" />
              </td>
            </tr>
            <tr>
              <td>
                <p>
                  <strong>General contrasts</strong> contrasts is currently a
                  "catch all" category, in which timbral changes signal boundary
                  creation, but the specific circumstances do not fall within
                  these categories. Segments annotated as general contrasts will
                  be revisited to discover new categories.
                </p>
                <p>ex. Mussorgsky, Ravel - Pictures At An Exhibition Mvt.III</p>
                <ReactAudioPlayer
                  src={
                    env.BACKEND_API_URL +
                    "/staticfiles/effect/first_data_model/excerpts/con2396.wav"
                  }
                  controls
                />
              </td>
              <td>
                <img src={antiphonalkey} alt="" />
              </td>
            </tr>
            <tr className="group">
              <td rowspan="2">Segmental</td>
              <td rowspan="2">Progressive orchestration</td>
              <td>
                <p>
                  <strong>Klangfarbenmelodie,</strong> a technique first
                  explicitly explored by Schoenberg and Webern, is the
                  succession of tone colours, analogous to pitches in a melody
                  (although often accompanied by pitch melody as well). In our
                  conception, it really serves a melodic function and could be
                  considered "timbre melody."
                </p>
                <p>ex. Berlioz - Symphonie Fantastique Mvt.IV</p>
                <ReactAudioPlayer
                  src={
                    env.BACKEND_API_URL +
                    "/staticfiles/effect/first_data_model/excerpts/pro2126.wav"
                  }
                  controls
                />
              </td>
              <td rowspan="2">
                <img src={progressivekey} alt="" />
              </td>
            </tr>
            <tr className="group">
              <td>
                <p>
                  <strong>Timbral modulation</strong> involves a succession of
                  gradually changing blended or integrated timbres, which is
                  capable of unifying all the transitional timbres into a
                  coherent grouping [adapted from Schnittke (2006)].
                </p>
                <p>ex. Mussorgsky, Ravel - Pictures At An Exhibition Mvt.XII</p>
                <ReactAudioPlayer
                  src={
                    env.BACKEND_API_URL +
                    "/staticfiles/effect/first_data_model/excerpts/pro2112.wav"
                  }
                  controls
                />
              </td>
            </tr>
            <tr>
              <td>Segmental (contrasts/progressive)</td>
              <td>Orchestral gestures</td>
              <td>
                <p>
                  <strong>Orchestral gestures</strong> are large-scale timbral
                  and textural changes that occur in a goal-directed manner,
                  creating a sense of agency and emotional force (Goodchild,
                  2016). The types are defined by changes in instrumentation in
                  terms of time course (gradual or sudden changes) and direction
                  (additive or reductive changes). The four types relate to the
                  descriptions in the literature of an orchestral crescendo
                  (gradual addition) , the reverse process (gradual reduction) ,
                  and timbral contrasts including a rapid switch to full forces
                  (sudden addition) and the drop-off to a contrast subgroup or
                  soloist (sudden reduction) .
                </p>
                <p>ex. Mahler - Symphony No.1 Mvt.IV (gradual addition)</p>
                <ReactAudioPlayer
                  src={
                    env.BACKEND_API_URL +
                    "/staticfiles/effect/first_data_model/excerpts/ges2042.wav"
                  }
                  controls
                />
                <p>ex. Debussy - La Mer Mvt.III (gradual reduction)</p>
                <ReactAudioPlayer
                  src={
                    env.BACKEND_API_URL +
                    "/staticfiles/effect/first_data_model/excerpts/ges2053.wav"
                  }
                  controls
                />
                <p>ex. Brahms - Symphony No.4 Mvt.III (sudden addition)</p>
                <ReactAudioPlayer
                  src={
                    env.BACKEND_API_URL +
                    "/staticfiles/effect/first_data_model/excerpts/ges2060.wav"
                  }
                  controls
                />
                <p>ex. Brahms - Symphony No.4 Mvt.I (sudden reduction)</p>
                <ReactAudioPlayer
                  src={
                    env.BACKEND_API_URL +
                    "/staticfiles/effect/first_data_model/excerpts/ges2054.wav"
                  }
                  controls
                />
              </td>
              <td>
                <img src={gesturekey} alt="" />
              </td>
            </tr>
          </table>
        </Paper>
        <br />
        <h2>Other database parameters</h2>
        <p>
          Blend and stratification effects are divided into two{" "}
          <strong>temporal categories</strong>: static and progressive. Static
          indicates that the instruments involved in the effect remain fixed,
          whereas progressive indicates that the instruments vary over time.
          Therefore, the temporal aspect of static and progressive describes how
          the orchestration evolves for these concurrent and sequential
          processes. The progressive category of orchestral effects (i.e.,
          Klangfarbenmelodie and timbral modulation) employs evolving
          orchestration at its core.
        </p>
        <p>
          Each effect has a <strong>strength rating</strong> (1 = weak; 5 =
          strong), which indicates how strong or weak the effect is perceived to
          be by our annotators. The factors that contribute to the strength may
          include the choice of instrumentation, its musical context, or
          features related to the performance or recording technology.
        </p>
        <br />
        <Paper className="about--paper">
          <h2 className="about--title" style={{ textAlign: "center" }}>
            References
          </h2>
          <p>
            Goodchild, M. (2016). Orchestral gestures: Music-theoretical
            perspectives and emotional responses (PhD dissertation). McGill
            University, Montreal, Canada.
          </p>
          <p>
            Sandell, G. J. (1995). Roles for spectral centroid and other factors
            in determining "blended" instrument pairings in orchestration. Music
            Perception, 13(2), 209â€“46.
          </p>
          <p>
            Schnittke, A. (2006). Timbral relationships and their functional
            use. In P. Matthews (Ed.), Orchestration: An anthology of writings.
            New York, NY: Routledge.
          </p>
        </Paper>
      </div>
    </div>
  );
}
